import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { APP_BASE_PATH, CLIENT_MOUNT_PATH } from './app/core/config/app-base';

/**
 * Pre-bootstrap URL normalizers — all four run BEFORE the Angular router
 * attaches, and each only ever rewrites the current history entry
 * (`replaceState`), so there is no extra request and no visible flash.
 *
 * The SPA uses PATH routing with the **site root** as its base
 * (`/properties/<id>`, no `#`, no `/app`). Catalyst still *serves* the files from
 * its `/app/` client mount — a gateway rule rewrites root paths onto it — so the
 * public base and the mount are different things (see `core/config/app-base.ts`).
 * Four consequences are handled here:
 *
 *  1. `normalizeClientMountPath` — anything that arrives at the mount
 *     (`/app/properties/<id>`, or the shell itself when 404.html re-fetches it)
 *     is rewritten to the public base, so every `/app/…` link already sitting in
 *     an inbox, a push payload or a native app build keeps resolving.
 *  2. `restoreSpaFallbackRoute` — Catalyst web client hosting serves files, not
 *     routes, so `/properties/<id>` has no file behind it and Catalyst answers
 *     with the 404 page. `client/public/404.html` hands the requested route over
 *     as `?__spa=…`; this puts it back in the address bar. See that file for why
 *     it cannot simply be a copy of index.html.
 *  3. `normalizeHashRoute` — every `/app/#/…` link already sent by email, push or
 *     a shared message keeps working: the fragment becomes the path.
 *  4. `normalizeApexDeepLink` — the notification catalog emits clean apex URLs
 *     (`https://app.nibni.com/<kind>[/<id>]`) which iOS/Android match for
 *     Universal / App Links and which the Public function 302s to the SPA. This
 *     is the safety net for the browser landing on the SPA at an apex-shaped path
 *     instead — a legacy `/n/<kind>` dispatch, or a deeplinkRedirect outage.
 */
const DEEPLINK_ID_KINDS = ['property', 'project', 'offer', 'reservation', 'lead', 'document'];
const DEEPLINK_STATIC_KINDS = ['team', 'partners', 'invitations', 'kyc', 'announcements', 'test'];
const DEEPLINK_KINDS = new Set([...DEEPLINK_ID_KINDS, ...DEEPLINK_STATIC_KINDS]);

/** Public base for app URLs — matches `angular.json`'s baseHref. '' = site root. */
const APP_BASE = APP_BASE_PATH;

function replaceAppUrl(path: string, search = '', hash = ''): void {
  const route = path.startsWith('/') ? path : `/${path}`;
  window.history.replaceState(null, '', `${window.location.origin}${APP_BASE}${route}${search}${hash}`);
}

/** Is the browser sitting on the bare SPA shell (as opposed to a real route)? */
function isShellPath(pathname: string): boolean {
  const route = stripMount(pathname);
  return route === '/' || route === '/index.html';
}

/** Pathname with the Catalyst client mount removed: `/app/x` → `/x`, `/app` → `/`. */
function stripMount(pathname: string): string {
  const path = String(pathname || '');
  if (!CLIENT_MOUNT_PATH || CLIENT_MOUNT_PATH === APP_BASE) return path || '/';
  if (path === CLIENT_MOUNT_PATH) return '/';
  if (path.startsWith(`${CLIENT_MOUNT_PATH}/`)) return path.slice(CLIENT_MOUNT_PATH.length) || '/';
  return path || '/';
}

function normalizeClientMountPath(): void {
  if (typeof window === 'undefined') return;
  try {
    const { pathname, search, hash } = window.location;
    if (!CLIENT_MOUNT_PATH || CLIENT_MOUNT_PATH === APP_BASE) return;
    if (pathname !== CLIENT_MOUNT_PATH && !pathname.startsWith(`${CLIENT_MOUNT_PATH}/`)) return;
    const route = stripMount(pathname);
    // `/app/index.html` is the shell, not a route.
    replaceAppUrl(route === '/index.html' ? '/' : route, search, hash);
  } catch {
    // no-op — never block bootstrap.
  }
}

function restoreSpaFallbackRoute(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const handed = params.get('__spa');
    if (!handed) return;
    params.delete('__spa');
    // `handed` is "<path>[?<query>]", exactly as 404.html captured it.
    const cut = handed.indexOf('?');
    const path = cut === -1 ? handed : handed.slice(0, cut);
    const handedQuery = cut === -1 ? '' : handed.slice(cut + 1);
    const search = [handedQuery, params.toString()].filter(Boolean).join('&');
    replaceAppUrl(path, search ? `?${search}` : '', window.location.hash);
  } catch {
    // no-op — never block bootstrap.
  }
}

function normalizeHashRoute(): void {
  if (typeof window === 'undefined') return;
  try {
    const { pathname, search, hash } = window.location;
    if (!hash.startsWith('#/')) return;
    // Only the shell can be carrying a legacy hash route; a real path route with
    // a fragment is an in-page anchor and must be left alone.
    if (!isShellPath(pathname)) return;
    const raw = hash.slice(1);
    const cut = raw.indexOf('?');
    const path = cut === -1 ? raw : raw.slice(0, cut);
    const hashQuery = cut === -1 ? '' : raw.slice(cut + 1);
    const merged = [search.replace(/^\?/, ''), hashQuery].filter(Boolean).join('&');
    replaceAppUrl(path, merged ? `?${merged}` : '');
  } catch {
    // no-op
  }
}

function normalizeApexDeepLink(): void {
  if (typeof window === 'undefined') return;
  try {
    const { pathname, search } = window.location;
    // Path we want to convert to a SPA route. May be:
    //   /<kind>[/<id>]            (catalog apex form)
    //   /n/<kind>[/<id>]          (legacy path form)
    //   /app/<kind>[/<id>]        (apex form with the SPA prefix)
    //   /app/n/<kind>[/<id>]      (legacy with the SPA prefix)
    let p = pathname;
    p = stripMount(p);
    if (APP_BASE && p.startsWith(`${APP_BASE}/`)) p = p.slice(APP_BASE.length);
    else if (APP_BASE && p === APP_BASE) p = '/';
    if (p.startsWith('/n/')) p = p.slice('/n'.length);
    const segments = p.replace(/^\/+/, '').split('/');
    const kind = segments[0] || '';
    if (!kind || !DEEPLINK_KINDS.has(kind)) return;
    const id = segments.length > 1 ? `/${segments.slice(1).join('/')}` : '';
    replaceAppUrl(`/n/${kind}${id}`, search);
  } catch {
    // no-op
  }
}

normalizeClientMountPath();
restoreSpaFallbackRoute();
normalizeHashRoute();
normalizeApexDeepLink();

async function removeLegacyServiceWorkers(): Promise<void> {
  if (typeof window === 'undefined') return;
  if (!('serviceWorker' in navigator)) return;

  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    // Keep the FCM messaging SW (`firebase-messaging-sw.js`) — it handles
    // background push notifications (registered by PushNotificationsService).
    await Promise.all(
      regs
        .filter((r) => {
          const script = String(r?.active?.scriptURL || r?.installing?.scriptURL || '');
          return !/firebase-messaging-sw\.js([?#]|$)/i.test(script);
        })
        .map((r) => r.unregister().catch(() => false))
    );
  } catch {
    // no-op
  }

  // Clear SW caches to prevent stale responses after unregister.
  try {
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } catch {
    // no-op
  }
}

void removeLegacyServiceWorkers();

bootstrapApplication(AppComponent, appConfig)
  .catch((err) => console.error(err));
